/**
 * V2MacrosPage (PRP-MCP-12 work item 7, G7): `/macros`, the user's own macros.
 *
 * Lists each macro (name, description, lines, source, last used); a click
 * opens its lines (code, description, action, quantity or calc, heading,
 * note). Lines are edited in place, added and removed; the macro is renamed
 * or deleted here. Upload takes a Xactimate macro export (.MCX) and shows what
 * was imported and what was skipped and why. Macros carry no price. Open to
 * free users: creating and viewing count nothing; applying one happens in chat.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { MACRO_ACTIONS, MACRO_ACTION_LABELS, MacrosAPI } from '../../lib/macrosApi';
import type {
  MacroAction,
  MacroDetail,
  MacroLine,
  MacroLineInput,
  MacroSummary,
  MacroUploadResult,
} from '../../lib/macrosApi';
import type { ApiError } from '../../types';
import { Banner } from '../components/ui/banner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';

const SOURCE_LABEL: Record<MacroSummary['source'], string> = {
  room: 'From a room',
  chat: 'Typed in chat',
  file: 'Macro file',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'Never' : d.toLocaleDateString();
}

function errText(e: unknown): string {
  return (e as ApiError)?.message || 'Something went wrong. Try again.';
}

/** "W" or "12" as typed: a number is a quantity, anything else a calc. */
function amountFields(text: string): Pick<MacroLineInput, 'quantity' | 'calc'> {
  const t = text.trim();
  if (!t) return { quantity: null, calc: null };
  return /^\d+(\.\d+)?$/.test(t) ? { quantity: Number(t), calc: null } : { quantity: null, calc: t };
}

function amountText(line: MacroLine): string {
  if (line.calc) return line.calc;
  return line.quantity != null ? String(line.quantity) : '';
}

interface Draft {
  code: string;
  action: MacroAction | '';
  amount: string;
  heading: string;
  note: string;
}

const EMPTY: Draft = { code: '', action: '', amount: '', heading: '', note: '' };

function draftOf(line: MacroLine): Draft {
  return {
    code: line.code,
    action: line.action ?? '',
    amount: amountText(line),
    heading: line.heading ?? '',
    note: line.note ?? '',
  };
}

/** The editable cells of one line (edit in place, or the add row). */
function LineEditor({
  draft,
  setDraft,
  onSave,
  onCancel,
  busy,
  testId,
}: {
  draft: Draft;
  setDraft: (_d: Draft) => void;
  onSave: () => void;
  onCancel?: () => void;
  busy: boolean;
  testId: string;
}) {
  return (
    <tr data-testid={testId} className="align-top">
      <td className="py-2 pr-2">
        <Input aria-label="Code" value={draft.code} placeholder="DRY 1/2"
          onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
      </td>
      <td className="py-2 pr-2 text-[12px] text-[var(--tx-faint)]">Set from the catalog</td>
      <td className="py-2 pr-2">
        <select aria-label="Action" value={draft.action}
          className="h-[34px] w-full rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-2 text-[13px] text-[var(--tx-hi)]"
          onChange={(e) => setDraft({ ...draft, action: e.target.value as MacroAction | '' })}>
          <option value="">Pick one</option>
          {MACRO_ACTIONS.map((a) => (
            <option key={a} value={a}>{MACRO_ACTION_LABELS[a]}</option>
          ))}
        </select>
      </td>
      <td className="py-2 pr-2">
        <Input aria-label="Quantity or calc" value={draft.amount} placeholder="12 or W"
          onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
      </td>
      <td className="py-2 pr-2">
        <Input aria-label="Heading" value={draft.heading}
          onChange={(e) => setDraft({ ...draft, heading: e.target.value })} />
      </td>
      <td className="py-2 pr-2">
        <Input aria-label="Note" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
      </td>
      <td className="whitespace-nowrap py-2">
        <Button size="sm" variant="primary" disabled={busy || !draft.code.trim() || !draft.action} onClick={onSave}>
          Save
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        )}
      </td>
    </tr>
  );
}

function toInput(d: Draft): MacroLineInput {
  return {
    code: d.code.trim(),
    action: d.action as MacroAction,
    ...amountFields(d.amount),
    heading: d.heading.trim() || null,
    note: d.note.trim() || null,
  };
}

/** One macro opened: rename, delete, and its lines. */
function MacroPanel({ macro, onChanged, onDeleted }: {
  macro: MacroDetail;
  onChanged: (_m: MacroDetail) => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(macro.name);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [adding, setAdding] = useState<Draft>(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setName(macro.name), [macro.name]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const reload = async () => onChanged(await MacrosAPI.get(macro.macro_id));

  return (
    <Card className="p-[18px] max-md:p-3" data-testid="macro-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label="Macro name" className="max-w-[320px]" value={name}
          onChange={(e) => setName(e.target.value)} />
        <Button size="sm" disabled={busy || !name.trim() || name.trim() === macro.name}
          onClick={() => run(async () => onChanged(await MacrosAPI.update(macro.macro_id, { name: name.trim() })))}>
          Rename
        </Button>
        <div className="ml-auto">
          {confirmDelete ? (
            <span className="flex items-center gap-2 text-[12.5px] text-[var(--tx-mid)]">
              Delete this macro?
              <Button size="sm" variant="danger" disabled={busy}
                onClick={() => run(async () => { await MacrosAPI.remove(macro.macro_id); onDeleted(); })}>
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button>
            </span>
          ) : (
            <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>Delete macro</Button>
          )}
        </div>
      </div>
      {macro.description && <p className="mb-0 mt-2 text-[12.5px] text-[var(--tx-lo)]">{macro.description}</p>}
      {error && <Banner tone="warn" className="mt-3">{error}</Banner>}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-[12.5px]">
          <thead className="text-[11px] uppercase tracking-[.05em] text-[var(--tx-faint)]">
            <tr>
              <th className="pb-2 pr-2">Code</th>
              <th className="pb-2 pr-2">Description</th>
              <th className="pb-2 pr-2">Action</th>
              <th className="pb-2 pr-2">Quantity or calc</th>
              <th className="pb-2 pr-2">Heading</th>
              <th className="pb-2 pr-2">Note</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody className="text-[var(--tx-hi)]">
            {macro.lines.map((ln) =>
              editing === ln.line_id ? (
                <LineEditor key={ln.line_id} testId={`line-edit-${ln.position}`} draft={draft} setDraft={setDraft}
                  busy={busy} onCancel={() => setEditing(null)}
                  onSave={() => run(async () => {
                    await MacrosAPI.updateLine(macro.macro_id, ln.line_id, toInput(draft));
                    setEditing(null);
                    await reload();
                  })} />
              ) : (
                <tr key={ln.line_id} data-testid={`line-${ln.position}`} className="border-t border-[var(--bd-subtle)] align-top">
                  <td className="py-2 pr-2 font-mono text-[12px]">{ln.code}</td>
                  <td className="py-2 pr-2">
                    {ln.description ?? '-'}
                    {ln.flags?.map((f) => (
                      <div key={f} className="text-[11.5px] text-[var(--warn)]">{f}</div>
                    ))}
                  </td>
                  <td className="py-2 pr-2">{ln.action ? MACRO_ACTION_LABELS[ln.action] : '-'}</td>
                  <td className="py-2 pr-2">{amountText(ln) || 'Asked when applied'}{ln.unit && !ln.calc && ln.quantity != null ? ` ${ln.unit}` : ''}</td>
                  <td className="py-2 pr-2">{ln.heading ?? '-'}</td>
                  <td className="py-2 pr-2 whitespace-pre-line">{ln.note ?? '-'}</td>
                  <td className="whitespace-nowrap py-2">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(ln.line_id); setDraft(draftOf(ln)); }}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy || macro.lines.length <= 1}
                      onClick={() => run(async () => onChanged(await MacrosAPI.removeLine(macro.macro_id, ln.line_id)))}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ),
            )}
            <LineEditor testId="line-add" draft={adding} setDraft={setAdding} busy={busy}
              onSave={() => run(async () => {
                await MacrosAPI.addLine(macro.macro_id, toInput(adding));
                setAdding(EMPTY);
                await reload();
              })} />
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function UploadResult({ result }: { result: MacroUploadResult }) {
  return (
    <div className="mt-3 space-y-1 text-[12.5px] text-[var(--tx-mid)]" data-testid="upload-result">
      {result.errors.map((e) => <p key={e} className="m-0 text-[var(--warn)]">{e}</p>)}
      {result.imported.map((m) => (
        <p key={m.macro_id} className="m-0">
          Imported {m.name}: {m.lines} lines.
          {m.unknown_codes.length > 0 && ` Not in the catalog: ${m.unknown_codes.join(', ')}.`}
          {m.lines_without_action > 0 && ` ${m.lines_without_action} need an action.`}
        </p>
      ))}
      {result.skipped.map((s) => (
        <p key={`${s.name}-${s.reason}`} className="m-0">Skipped {s.name}: {s.reason}</p>
      ))}
      {result.prices_dropped > 0 && (
        <p className="m-0">Prices in the file were not imported ({result.prices_dropped}).</p>
      )}
    </div>
  );
}

/** The Macros page. */
export default function V2MacrosPage() {
  const [macros, setMacros] = useState<MacroSummary[] | null>(null);
  const [open, setOpen] = useState<MacroDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<MacroUploadResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setMacros((await MacrosAPI.list()).macros);
    } catch (e) {
      setError(errText(e));
      setMacros([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openMacro = async (id: string) => {
    setError(null);
    try {
      setOpen(await MacrosAPI.get(id));
    } catch (e) {
      setError(errText(e));
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await MacrosAPI.upload(file));
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1040px] px-6 py-8 max-md:px-4" data-testid="macros-page">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="m-0 text-[22px] font-[620] tracking-[-0.01em] text-[var(--tx-hi)]">Macros</h1>
        <div className="ml-auto">
          <input ref={fileRef} type="file" accept=".mcx,.MCX,.zip" className="hidden" data-testid="macro-file"
            onChange={(e) => void onFile(e.target.files?.[0])} />
          <Button variant="primary" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? 'Uploading...' : 'Upload macro file'}
          </Button>
        </div>
      </div>
      <p className="mb-4 mt-0 text-[12.5px] text-[var(--tx-lo)]">
        Your saved lists of line items. Apply one to rooms from your chat app. Macros hold no prices, and only you
        can see yours.
      </p>
      {error && <Banner tone="warn" className="mb-3">{error}</Banner>}
      {result && <UploadResult result={result} />}
      <Card className="mt-3 p-0">
        {macros === null ? (
          <p className="m-0 p-[18px] text-[12.5px] text-[var(--tx-lo)]">Loading...</p>
        ) : macros.length === 0 ? (
          <p className="m-0 p-[18px] text-[12.5px] text-[var(--tx-lo)]" data-testid="macros-empty">
            No macros yet. Upload a Xactimate macro file, or ask your chat app to save a room as a macro.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-[12.5px]">
              <thead className="text-[11px] uppercase tracking-[.05em] text-[var(--tx-faint)]">
                <tr>
                  <th className="px-[18px] py-2">Name</th>
                  <th className="py-2 pr-2">Description</th>
                  <th className="py-2 pr-2">Lines</th>
                  <th className="py-2 pr-2">Source</th>
                  <th className="py-2 pr-[18px]">Last used</th>
                </tr>
              </thead>
              <tbody>
                {macros.map((m) => (
                  <tr key={m.macro_id} data-testid={`macro-${m.macro_id}`}
                    className={`cursor-pointer border-t border-[var(--bd-subtle)] hover:bg-[var(--bg-3)] ${open?.macro_id === m.macro_id ? 'bg-[var(--bg-3)]' : ''}`}
                    onClick={() => void openMacro(m.macro_id)}>
                    <td className="px-[18px] py-2 font-[550] text-[var(--tx-hi)]">
                      <button type="button" className="text-left" onClick={(e) => { e.stopPropagation(); void openMacro(m.macro_id); }}>
                        {m.name}
                      </button>
                    </td>
                    <td className="py-2 pr-2 text-[var(--tx-mid)]">{m.description ?? '-'}</td>
                    <td className="py-2 pr-2">{m.lines}</td>
                    <td className="py-2 pr-2">{SOURCE_LABEL[m.source] ?? m.source}</td>
                    <td className="py-2 pr-[18px]">{fmtDate(m.last_used_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {open && (
        <div className="mt-4">
          <MacroPanel key={open.macro_id} macro={open}
            onChanged={(m) => { setOpen(m); void load(); }}
            onDeleted={() => { setOpen(null); void load(); }} />
        </div>
      )}
    </div>
  );
}
